// Arma el objeto `Rol` de SPEC.md §2 desde lo que la app YA tiene: el torneo (con su liga
// y sus campos), la jornada (get_jornadas_detalle) y las filas de get_rol_jornada.
// Solo lectura, sin viajes nuevos a la base. Lo que el spec asume y la base no tiene
// (superficie, tolerancia, coordinador, version) no se inventa: se deja vacio y la
// plantilla no lo pinta. Decisiones con el dueno (2026-09-07): sin hora de fin; el
// numero del juego viene de la base (torneos_juegos.numero) y se imprime "J7 · #37";
// un club con dos grupos en la misma categoria muestra el grupo.
// Desde 20261005 cada categoria del rol es una DIVISION (categoria + genero): U14 Femenil
// y U14 Varonil son dos categorias del rol, con su color. Las filas sin genero o sin
// division se ven y agrupan como antes, por su categoria.

import { FLYER_PALETTE } from '../lib/rolFlyer';
import { etiquetaDivision, idDivision, nombreConRama, nombreIncluyeRama, normalizarRama } from '../lib/division';
import { nombreCorto } from './rol-template';

export const MAX_ETIQUETA = 11;

const hhmm = (h) => (h ? String(h).slice(0, 5) : null);

// "U14 · Femenil" (o el nombre solo, si la fila no trae genero).
const nombreDivision = (r) => etiquetaDivision({ nombre: r.categoria_nombre || 'Sin categoría', rama: r.rama });

// Oscurece un hex para el tema claro (el volt sobre fondo claro es invisible, SPEC §7).
export const aColorClaro = (hex, factor = 0.62) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || ''));
  if (!m) return '#333333';
  const n = parseInt(m[1], 16);
  const c = (v) => Math.round(v * factor).toString(16).padStart(2, '0');
  return `#${c((n >> 16) & 255)}${c((n >> 8) & 255)}${c(n & 255)}`;
};

// "Sub-12" → "SUB-12"; "Libre varonil" → "LIBRE VAR."; "U14 Femenil" → "U14 FEM."
export const etiquetaCorta = (nombre) => {
  const limpio = String(nombre || 'Sin categoría').trim();
  const may = limpio.toUpperCase();
  if (may.length <= MAX_ETIQUETA) return may;
  const partes = may.split(/\s+/);
  if (partes.length >= 2) {
    const abrev = `${partes[0]} ${partes[1].slice(0, 3)}.`;
    if (abrev.length <= MAX_ETIQUETA) return abrev;
  }
  return may.slice(0, MAX_ETIQUETA);
};

// Etiqueta corta de una division. Si su categoria solo tiene una division en la jornada,
// la de siempre (etiquetaCorta del nombre). Si hay varias, el genero se conserva solo
// cuando hace falta para distinguirlas: completo si cabe ("U14 FEMENIL"), con inicial
// si no ("U12 TAC. F").
const etiquetaCortaDivision = (base, rama, variasDivisiones) => {
  const nombre = base || 'Sin categoría';
  if (!variasDivisiones || nombreIncluyeRama(nombre, rama)) return etiquetaCorta(nombre);
  const completa = nombreConRama({ nombre, rama }).toUpperCase();
  if (completa.length <= MAX_ETIQUETA) return completa;
  const inicial = (normalizarRama(rama) || '').charAt(0);
  if (!inicial) return etiquetaCorta(nombre);
  // Abreviatura forzada ("U12 Tackle" -> "U12 TAC."), para que quepa la inicial.
  const partes = nombre.trim().toUpperCase().split(/\s+/);
  const abrev = partes.length >= 2 ? `${partes[0]} ${partes[1].slice(0, 3)}.` : partes[0];
  return `${abrev.slice(0, MAX_ETIQUETA - 2).trimEnd()} ${inicial}`;
};

// El grupo sin el nombre del club adelante: "Halcones Demo U14 Fem" → "U14 Fem".
const restoDelGrupo = (grupo, club) => {
  const g = String(grupo || '').trim();
  const c = String(club || '').trim();
  if (!g) return '';
  if (c && g.toLowerCase().startsWith(c.toLowerCase())) return g.slice(c.length).trim();
  return g;
};

// Los "clubes" del rol son las INSCRIPCIONES (club + grupo), indexadas por inscripcion:
// asi dos grupos del mismo club en la misma categoria son dos entradas distintas.
const armarClubes = (filas) => {
  const porInsc = new Map();
  const lados = (f) => [
    { id: f.local_inscripcion_id, club: f.local_club, short: f.local_short, logo: f.local_logo, grupo: f.local_grupo, cat: idDivision(f) },
    { id: f.visita_inscripcion_id, club: f.visita_club, short: f.visita_short, logo: f.visita_logo, grupo: f.visita_grupo, cat: idDivision(f) },
  ];
  filas.forEach((f) => lados(f).forEach((l) => { if (l.id && !porInsc.has(l.id)) porInsc.set(l.id, l); }));

  // ¿Cuantas inscripciones trae cada club en cada division?
  const conteo = new Map();
  porInsc.forEach((l) => {
    const k = `${l.cat}|${(l.club || '').toLowerCase()}`;
    conteo.set(k, (conteo.get(k) || 0) + 1);
  });

  const clubes = {};
  porInsc.forEach((l, id) => {
    const nombre = l.club || 'Por definir';
    const repetido = (conteo.get(`${l.cat}|${nombre.toLowerCase()}`) || 0) > 1;
    const base = l.short || nombreCorto({ nombre });
    const resto = repetido ? restoDelGrupo(l.grupo, l.club) : '';
    clubes[id] = {
      id,
      nombre: repetido && resto ? `${nombre} (${resto})` : nombre,
      nombre_corto: repetido && resto ? `${base} ${resto}` : (l.short || undefined),
      escudo_url: l.logo || undefined,
    };
  });
  return clubes;
};

/**
 * @param {object} p
 * @param {object} p.torneo        fila de torneos con `ligas`, `campos`, `sede`, `nombre`
 * @param {object} [p.jornadaInfo] fila de get_jornadas_detalle (numero, nombre, fecha, sede)
 * @param {number} p.jornada       numero de jornada
 * @param {Array}  p.filas         filas de get_rol_jornada
 * @param {Array}  [p.inscripciones] inscripciones aceptadas del torneo, para los descansos:
 *                                 { inscripcion_id, convocatoria_id | categoria_id | categoria (nombre), club_nombre, grupo_nombre, club_logo_url, club_short }
 * @param {string} [p.organizadora] nombre de la organizadora (si el torneo no tiene liga)
 * @param {string} [p.generadoEn]  ISO
 * @returns {{ rol: object, clubes: Record<string, object> }}
 */
export function armarRol({ torneo, jornadaInfo, jornada, filas, inscripciones = [], organizadora, generadoEn }) {
  const f = (Array.isArray(filas) ? filas : []).filter(Boolean);

  // Campos: los del torneo en su orden; si un juego usa uno que no esta, va al final.
  const nombresCampo = [];
  (Array.isArray(torneo?.campos) ? torneo.campos : []).forEach((c) => {
    const n = String(c?.nombre || '').trim();
    if (n && !nombresCampo.includes(n)) nombresCampo.push(n);
  });
  f.forEach((r) => {
    const n = String(r.campo || '').trim() || 'Sin cancha';
    if (!nombresCampo.includes(n)) nombresCampo.push(n);
  });
  const campoId = (n) => `c${nombresCampo.indexOf(n) + 1}`;
  const campos = nombresCampo.map((n) => ({ id: campoId(n), nombre: n, superficie: '' }));

  const horarios = Array.from(new Set(f.map((r) => hhmm(r.hora)).filter(Boolean))).sort();

  // Categorias del rol = divisiones. Color estable por orden alfabetico de la etiqueta,
  // como el flyer actual.
  const infoDiv = new Map();
  f.forEach((r) => {
    const nombre = nombreDivision(r);
    const id = idDivision(r);
    if (!infoDiv.has(nombre)) infoDiv.set(nombre, { id, base: r.categoria_nombre, rama: r.rama, cat: r.categoria_id || r.categoria_nombre || '' });
    else if (!infoDiv.get(nombre).id && id) infoDiv.get(nombre).id = id;
  });
  const divisionesPorCategoria = new Map();
  infoDiv.forEach((info) => divisionesPorCategoria.set(info.cat, (divisionesPorCategoria.get(info.cat) || 0) + 1));
  const categorias = Array.from(infoDiv.keys()).sort().map((nombre, i) => {
    const oscuro = FLYER_PALETTE[i % FLYER_PALETTE.length];
    const info = infoDiv.get(nombre);
    return {
      id: info.id || `cat-${i + 1}`,
      nombre,
      etiqueta_corta: etiquetaCortaDivision(info.base, info.rama, divisionesPorCategoria.get(info.cat) > 1),
      color_oscuro: oscuro,
      color_claro: aColorClaro(oscuro),
    };
  });
  const catPorNombre = Object.fromEntries(categorias.map((c) => [c.nombre, c.id]));

  const clubes = armarClubes(f);

  const partidos = f
    .filter((r) => r.local_inscripcion_id && r.visita_inscripcion_id && hhmm(r.hora))
    .map((r) => ({
      id: r.numero != null ? `J${jornada} · #${r.numero}` : `J${jornada}`,
      numero: r.numero ?? null,
      juego_id: r.juego_id,
      campo_id: campoId(String(r.campo || '').trim() || 'Sin cancha'),
      horario: hhmm(r.hora),
      categoria_id: catPorNombre[nombreDivision(r)],
      local_id: r.local_inscripcion_id,
      visitante_id: r.visita_inscripcion_id,
    }));

  // Descansos: inscripciones aceptadas de una categoria que si se juega hoy y no aparecen.
  const juegan = new Set(partidos.flatMap((p) => [p.local_id, p.visitante_id]));
  const catsHoy = new Set(partidos.map((p) => p.categoria_id));
  // Una inscripcion se liga a su division por id. Las que solo traen la categoria (id o
  // nombre) se ligan si esa categoria tiene UNA sola division en la jornada; con varias
  // no se adivina.
  const divisionUnicaPor = (clave) => {
    const m = new Map();
    f.forEach((r) => {
      const k = clave(r);
      if (!k) return;
      const id = catPorNombre[nombreDivision(r)];
      m.set(k, m.has(k) && m.get(k) !== id ? null : id);
    });
    return m;
  };
  const divPorCategoriaId = divisionUnicaPor((r) => r.categoria_id);
  const divPorCategoriaNombre = divisionUnicaPor((r) => r.categoria_nombre);
  const descansosPorCat = new Map();
  (inscripciones || []).forEach((i) => {
    // La pantalla de juegos trae la categoria por nombre (equiposMap); otras fuentes por id.
    const catDeInsc = i?.convocatoria_id
      || (i?.categoria_id && divPorCategoriaId.get(i.categoria_id))
      || catPorNombre[i?.categoria]
      || (i?.categoria && divPorCategoriaNombre.get(i.categoria));
    if (!i?.inscripcion_id || juegan.has(i.inscripcion_id) || !catsHoy.has(catDeInsc)) return;
    if (!clubes[i.inscripcion_id]) {
      clubes[i.inscripcion_id] = {
        id: i.inscripcion_id,
        nombre: i.club_nombre || 'Por definir',
        nombre_corto: i.club_short || undefined,
        escudo_url: i.club_logo_url || undefined,
      };
    }
    if (!descansosPorCat.has(catDeInsc)) descansosPorCat.set(catDeInsc, []);
    descansosPorCat.get(catDeInsc).push(i.inscripcion_id);
  });
  const descansos = Array.from(descansosPorCat, ([categoria_id, club_ids]) => ({ categoria_id, club_ids }));

  const rol = {
    liga: torneo?.ligas?.nombre || organizadora || torneo?.organizador || '',
    torneo: torneo?.nombre || '',
    logo_url: torneo?.logo_url || torneo?.ligas?.logo_url || null,
    jornada: Number(jornada) || jornadaInfo?.numero || 0,
    fecha: jornadaInfo?.fecha || f.find((r) => r.fecha)?.fecha || null,
    sede: jornadaInfo?.sede || f.find((r) => r.sede)?.sede || torneo?.sede || '',
    version: null,
    generado_en: generadoEn || new Date().toISOString(),
    tolerancia_min: null,
    campos,
    horarios,
    categorias,
    partidos,
    descansos,
  };
  return { rol, clubes };
}

const SIN_SEDE_ROL = 'Sin sede';
const ordenNaturalRol = (a, b) => String(a).localeCompare(String(b), 'es', { numeric: true, sensitivity: 'base' });

/**
 * Un rol por sede. Con una sola sede en las filas devuelve un rol: si el torneo tiene 2 o mas sedes y esa
 * esta en `sedes`, con sus canchas activas y "Nombre · direccion" (la jornada puede jugarse entera en una
 * sede que no es la principal); si no (torneo de una sede, sede desconocida o sin sedes), `[armarRol(p)]`,
 * igual que antes.
 * Con varias, uno por sede (en el orden de `sedes`, luego orden natural, "Sin sede" al final): solo las
 * filas de esa sede, `torneo.campos` = las canchas de esa sede y `rol.sede` = "Nombre · direccion"
 * (un enlace no se abre desde una imagen, asi que el subtitulo lleva la direccion).
 * Los descansos son los equipos que no juegan en ninguna sede esa jornada, en cada imagen de una
 * sede donde su division juega.
 * @param {object} p  lo de armarRol mas `sedes`: [{ nombre, direccion, canchas: [{ nombre, activa }] }]
 * @returns {Array<{ rol: object, clubes: Record<string, object>, sede?: string }>}
 */
export function armarRolesPorSede(p) {
  const { sedes = [], ...resto } = p;
  const filas = (Array.isArray(p.filas) ? p.filas : []).filter(Boolean);
  const nombreSede = (r) => String(r.sede || '').trim() || SIN_SEDE_ROL;
  const presentes = new Set(filas.map(nombreSede));
  const lista = Array.isArray(sedes) ? sedes.filter(Boolean) : [];
  const canchasDe = (datos) => (datos?.canchas || []).filter((c) => c && c.activa !== false).map((c) => ({ nombre: c.nombre }));
  const tituloDe = (nombre, datos) => (datos?.direccion ? `${nombre} · ${datos.direccion}` : nombre);

  if (presentes.size <= 1) {
    const unica = [...presentes][0];
    // Solo en un torneo de 2+ sedes: con una sede queda igual que hoy (spec §4.8).
    const datos = lista.length > 1 && unica && unica !== SIN_SEDE_ROL ? lista.find((s) => s.nombre === unica) : null;
    if (!datos) return [armarRol(resto)];
    return [armarRol({
      ...resto,
      torneo: { ...(p.torneo || {}), campos: canchasDe(datos) },
      jornadaInfo: { ...(p.jornadaInfo || {}), sede: tituloDe(unica, datos) },
    })];
  }

  const enOrden = lista.map((s) => s.nombre).filter((n) => presentes.has(n) && n !== SIN_SEDE_ROL);
  const otras = [...presentes].filter((n) => !enOrden.includes(n) && n !== SIN_SEDE_ROL).sort(ordenNaturalRol);
  const nombres = [...new Set([...enOrden, ...otras])];
  if (presentes.has(SIN_SEDE_ROL)) nombres.push(SIN_SEDE_ROL);

  // Quien juega en cualquier sede ese dia no descansa.
  const juegan = new Set();
  filas.forEach((r) => {
    if (r.local_inscripcion_id && r.visita_inscripcion_id && hhmm(r.hora)) {
      juegan.add(r.local_inscripcion_id);
      juegan.add(r.visita_inscripcion_id);
    }
  });
  const descansan = (p.inscripciones || []).filter((i) => !juegan.has(i?.inscripcion_id));

  return nombres.map((nombre) => {
    const datos = lista.find((s) => s.nombre === nombre);
    const armado = armarRol({
      ...resto,
      torneo: { ...(p.torneo || {}), campos: canchasDe(datos) },
      jornadaInfo: { ...(p.jornadaInfo || {}), sede: tituloDe(nombre, datos) },
      filas: filas.filter((r) => nombreSede(r) === nombre),
      inscripciones: descansan,
    });
    return { ...armado, sede: nombre };
  });
}
